import { useMemo } from 'react'

import { Box, Grid } from '@mui/material'
import { useFormContext, useFormState } from 'react-hook-form'

import {
  ControlledAutocomplete,
  ControlledCheckbox,
  ControlledTextField,
  IAutocompleteOptions,
} from 'components/common'
import {
  ReplaceBookingDriverDtoInput,
  useGetMyVehiclesQuery,
  useGetSupplierAllSubDriversQuery,
} from 'generated/graphql'

interface IReplaceDriverFormProps {
  allocatedSubDriverIds: number[]
}

export const ReplaceDriverForm = ({ allocatedSubDriverIds }: IReplaceDriverFormProps) => {
  const { control } = useFormContext<ReplaceBookingDriverDtoInput>()

  const { errors } = useFormState({ control })

  const { data: vehiclesData, loading: vehiclesLoading } = useGetMyVehiclesQuery()

  const { data: supplierSubDriversData, loading: supplierSubDriversLoading } =
    useGetSupplierAllSubDriversQuery()

  const vehicleOptions: IAutocompleteOptions = useMemo(() => {
    return vehiclesData?.myVehicles
      ? vehiclesData?.myVehicles?.map((vehicle) => ({
          label: `${vehicle.vehicleType.name} - ${vehicle.registrationNumber}`,
          value: vehicle.id,
        }))
      : []
  }, [vehiclesData?.myVehicles])

  const driverOptions: IAutocompleteOptions = useMemo(() => {
    return supplierSubDriversData?.supplierAllSubDrivers
      ? supplierSubDriversData?.supplierAllSubDrivers?.map((driver) => ({
          label: driver.name,
          value: driver.id,
          disabled: allocatedSubDriverIds.includes(Number(driver.id)),
        }))
      : []
  }, [allocatedSubDriverIds, supplierSubDriversData?.supplierAllSubDrivers])

  return (
    <>
      <Box pt='6px'>
        <Grid container columnSpacing={3} rowSpacing={1.5}>
          <Grid item xs={12} sm={6}>
            <ControlledAutocomplete
              required
              name='driverId'
              label='Driver'
              loading={supplierSubDriversLoading}
              options={driverOptions}
              control={control}
              error={!!errors.driverId}
              helperText={errors.driverId?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledAutocomplete
              required
              control={control}
              name='vehicleId'
              label='Vehicle'
              loading={vehiclesLoading || supplierSubDriversLoading}
              disabled={vehiclesLoading}
              options={vehicleOptions}
              error={!!errors.vehicleId}
              helperText={errors.vehicleId?.message}
            />
          </Grid>

          <Grid item xs={12}>
            <ControlledCheckbox
              control={control}
              label='Show Instruction to Driver'
              name='isShownInstruction'
            />
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField
              control={control}
              label='Instruction'
              name='instruction'
              multiline
              rows={7}
              error={!!errors.instruction}
              helperText={errors.instruction?.message}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}
